import { useEffect, useState } from 'react';

import { DateTime } from 'luxon';
import { useFlags } from 'launchdarkly-react-client-sdk';

import AppointmentCard from '~/components/common/calendar/AppointmentCard';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { useConsolidatedScheduleItems } from '~/lib/hooks/useConsolidatedScheduleItems';
import { useGroupEventMeetingDetails } from '~/lib/hooks/useGroupEventMeetingDetails';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import useAppointments from '~/lib/hooks/useAppointments';
import {
  deriveScheduleItems,
  ScheduleType,
} from '~/lib/util/schedule/schedule.util';
import { useGetAppointmentDetailsQuery } from '~/store/services/appointment';
import { useGetGroupClassEventsQuery } from '~/store/services/groupClass';

import { RailSection } from './RailSection';
import { RailSectionErrorLabel } from './rightRail.constants';
import { getUpcomingAppointmentsStartISO, isUpcoming } from './rightRail.util';

const CLOCK_TICK_MS = 60_000;

interface UpcomingAppointmentsSectionProps {
  patientId: string;
}

const UpcomingAppointmentsSection = ({
  patientId,
}: UpcomingAppointmentsSectionProps) => {
  const { currentUser } = useGlobalContext();
  const { getZoomUrl } = useAppointments();
  const { openGroupEventMeeting } = useGroupEventMeetingDetails();
  const { showErrorToast } = useErrorToast();
  // Fixed at mount so the query args stay stable.
  const [startISO] = useState(() =>
    getUpcomingAppointmentsStartISO(DateTime.now()),
  );
  const [now, setNow] = useState(() => DateTime.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const {
    data: appointmentDetails,
    isError: isAppointmentsError,
    isLoading: isAppointmentsLoading,
  } = useGetAppointmentDetailsQuery(
    {
      startDateISO: startISO,
      patientId,
      statuses: [AppointmentStatus.SCHEDULED],
      pageSize: 50,
    },
    { skip: !patientId },
  );

  const { groupsRestructuring: isGroupsRestructuringEnabled } =
    useFlags<LDFlags>();
  // With groupsRestructuring on, useConsolidatedScheduleItems drops these for group events V2.
  const {
    data: groupClassEvents,
    isError: isGroupClassEventsError,
    isLoading: isGroupClassEventsLoading,
  } = useGetGroupClassEventsQuery(
    { pageSize: 50, patientExternalId: patientId, startDateTime: startISO },
    { skip: !patientId || isGroupsRestructuringEnabled },
  );

  // Rebuilt every render so each minute tick refreshes Join, which reads the clock.
  const derivedScheduleItems = deriveScheduleItems(
    currentUser?.userId,
    appointmentDetails?.data,
    groupClassEvents?.data,
    [],
  );

  // Group rows come from the Appointments tab's flag-aware source, so their ids
  // match the Join endpoint.
  const {
    consolidatedScheduleItems,
    isGroupEventsError,
    isLoading: isGroupEventsV2Loading,
  } = useConsolidatedScheduleItems({
    filterByValue: currentUser?.userId,
    pageSize: 50,
    patientId,
    scheduleItems: derivedScheduleItems,
    shouldIncludeNoteStatus: false,
    startDateTime: startISO,
  });

  const scheduleItems = consolidatedScheduleItems.filter((item) =>
    isUpcoming(item, now),
  );

  const handleJoin = (item: (typeof scheduleItems)[number]) => {
    if (item.typeOf === ScheduleType.APPOINTMENT) {
      getZoomUrl(
        item.externalId,
        (url: string) => {
          if (url) window.open(url, '_blank', 'noopener');
        },
        (error: string, statusCode: number) =>
          showErrorToast(
            GlobalErrorCode.GET_ZOOM_MEETING_DETAILS,
            statusCode,
            error,
          ),
      );
    } else {
      openGroupEventMeeting(item.externalId);
    }
  };

  const rows = scheduleItems.map((item) => {
    const host = item.hosts?.[0];
    return (
      <AppointmentCard
        avatarProps={
          item.typeOf === ScheduleType.APPOINTMENT && host
            ? {
                name: `${host.firstName ?? ''} ${host.lastName ?? ''}`.trim(),
                src: host.avatar?.url,
                userType: 'provider',
              }
            : undefined
        }
        endTimeISO={item.endDateTime}
        id={`overview-appt__${item.externalId}`}
        isFlat
        key={item.externalId}
        onJoin={item.isShowZoomButton ? () => handleJoin(item) : undefined}
        startTimeISO={item.startDateTime}
        title={item.title}
      />
    );
  });

  return (
    <RailSection
      action={{
        linkTo: PatientTabsCollection[PatientTabs.APPOINTMENTS].subPath,
        patientId,
        rightLinkText: 'View all',
      }}
      emptyCaption="No upcoming appointments"
      errorLabel={RailSectionErrorLabel.Appointments}
      headerText="Upcoming appointments"
      id="patient-overview-right-rail-v2__appointments"
      isError={
        isAppointmentsError || isGroupClassEventsError || isGroupEventsError
      }
      isLoading={
        isAppointmentsLoading ||
        isGroupClassEventsLoading ||
        isGroupEventsV2Loading
      }
      rows={rows}
    />
  );
};

export default UpcomingAppointmentsSection;
